import React, { useCallback } from 'react';
import Joyride, { STATUS, EVENTS } from 'react-joyride';
import { useJoyrideStore } from '@/stores/joyride/joyrideStore';
import useLayoutStore from '@/stores/layout/layoutStore';
import JoyrideCustomTooltip from '@/components/JoyrideCustomTooltip';
import { scrollTargetIntoView } from '@/utils/joyrideScrolling';
import mixpanel from '@/utils/mixpanel';

const GlobalJoyride = () => {
  const run = useJoyrideStore((state) => state.run);
  const steps = useJoyrideStore((state) => state.steps);
  const stepIndex = useJoyrideStore((state) => state.stepIndex);
  const setStepIndex = useJoyrideStore((state) => state.setStepIndex);
  const stopTour = useJoyrideStore((state) => state.stopTour);
  const markTourCompleted = useJoyrideStore((state) => state.markTourCompleted);
  const scrollContainerSelector = useJoyrideStore((state) => state.scrollContainerSelector);
  const isDarkMode = useLayoutStore((state) => state.isDarkMode);

  const tooltipBg = isDarkMode ? '#2C2A46' : '#ffffff';
  const tooltipText = isDarkMode ? '#F3F4F6' : '#111827';
  const tooltipSecondary = isDarkMode ? '#9CA3AF' : '#6B7280';

  const activeTourKey = useJoyrideStore((state) => state.activeTourKey);

  const handleCallback = useCallback(
    (data) => {
      const { status, type, action, index, step } = data;
      const tourKey = step?.tourKey || activeTourKey || steps?.[0]?.tourKey || 'unknown';
      const totalSteps = steps?.length || 0;

      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        const wasSkipped = status === STATUS.SKIPPED;
        mixpanel.track('Joyride Tour Completed', {
          tour_key: tourKey,
          skipped: wasSkipped,
          steps_viewed: (index ?? 0) + 1,
          total_steps: totalSteps,
        });

        if (step && step.tourKey) {
          markTourCompleted(step.tourKey);
        } else if (steps && steps.length > 0 && steps[0].tourKey) {
          markTourCompleted(steps[0].tourKey);
        }
        stopTour();
        return;
      }

      if (type === EVENTS.STEP_AFTER && typeof index === 'number') {
        mixpanel.track('Joyride Step Completed', {
          tour_key: tourKey,
          step_index: index + 1,
          step_title: step?.title || null,
          total_steps: totalSteps,
          action,
        });

        const nextIndex = action === 'prev' ? Math.max(0, index - 1) : index + 1;
        setStepIndex(nextIndex);

        // Scroll the next step's target into view within the scroll container
        const nextStep = steps?.[nextIndex];
        if (nextStep?.target) {
          setTimeout(() => {
            scrollTargetIntoView(nextStep.target, scrollContainerSelector);
          }, 100);
        }
      }
    },
    [markTourCompleted, stopTour, setStepIndex, steps, scrollContainerSelector, activeTourKey]
  );

  if (!run || !steps || steps.length === 0) {
    return null;
  }

  const isBubblePreviewTour = activeTourKey === 'bubble-preview';

  return (
    <Joyride
      run={run}
      steps={steps}
      stepIndex={stepIndex}
      continuous={!isBubblePreviewTour}
      showProgress={!isBubblePreviewTour}
      showSkipButton={!isBubblePreviewTour}
      disableScrolling
      scrollToFirstStep={false}
      disableOverlayClose
      disableCloseOnEsc={isBubblePreviewTour}
      callback={handleCallback}
      tooltipComponent={JoyrideCustomTooltip}
      styles={{
        options: {
          zIndex: 9999,
          primaryColor: '#5745FA',
          backgroundColor: tooltipBg,
          textColor: tooltipText,
          arrowColor: tooltipBg,
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          width: 380,
        },
        tooltip: {
          fontFamily: 'Inter, "Inter Variable", ui-sans-serif, system-ui, sans-serif',
          fontSize: 13,
          lineHeight: 1.25,
          borderRadius: 12,
          boxSizing: 'border-box',
          padding: 0,
        },
        tooltipContainer: {
          fontFamily: 'Inter, "Inter Variable", ui-sans-serif, system-ui, sans-serif',
          fontSize: 13,
          lineHeight: 1.25,
          textAlign: 'left',
        },
        tooltipContent: {
          fontFamily: 'Inter, "Inter Variable", ui-sans-serif, system-ui, sans-serif',
          fontSize: 13,
          lineHeight: 1.25,
          padding: '20px 20px 16px',
        },
        tooltipTitle: {
          fontFamily: 'Inter, "Inter Variable", ui-sans-serif, system-ui, sans-serif',
          fontSize: 14,
          lineHeight: 1.4,
          fontWeight: 600,
          padding: '20px 20px 0',
        },
        tooltipFooter: {
          marginTop: 12,
          paddingLeft: 20,
          paddingRight: 20,
          paddingBottom: 20,
        },
        buttonNext: {
          backgroundColor: 'var(--v3-purple-primary, #5745FA)',
          border: '1px solid var(--v3-purple-primary, #5745FA)',
          borderRadius: 12,
          color: '#fff',
          fontFamily: 'Inter, "Inter Variable", ui-sans-serif, system-ui, sans-serif',
          fontSize: 13,
          lineHeight: '16px',
          fontWeight: 500,
          padding: '10px 24px',
          outline: 'none',
          cursor: 'pointer',
        },
        buttonBack: {
          backgroundColor: 'transparent',
          border: 0,
          borderRadius: 12,
          color: '#5745FA',
          fontFamily: 'Inter, "Inter Variable", ui-sans-serif, system-ui, sans-serif',
          fontSize: 13,
          lineHeight: '16px',
          fontWeight: 500,
          padding: 8,
          cursor: 'pointer',
        },
        buttonSkip: {
          backgroundColor: 'transparent',
          border: 0,
          color: tooltipSecondary,
          fontFamily: 'Inter, "Inter Variable", ui-sans-serif, system-ui, sans-serif',
          fontSize: 13,
          lineHeight: '16px',
          fontWeight: 500,
          padding: 8,
          cursor: 'pointer',
        },
        buttonClose: {
          color: tooltipSecondary,
          transform: 'scale(0.7)',
          transformOrigin: 'top right',
          display: 'none',
        },
        spotlight: {
          borderRadius: 12,
        },
      }}
    />
  );
};

export default GlobalJoyride;
